import { MAIN_CONTENT_DRAFT_MAX_LENGTH, nextLengthLimitedValue } from '@/constants/meeting'
import { fmt } from '@/constants/ui-locale'
import { useUiStrings } from '@/hooks/useUiStrings'
import type { LiveMemoStatus } from '@/hooks/useLiveMemoInsights'
import type { MainContentUpdateStatus } from '@/hooks/useProxySttRecording'
import Renderer from '@/components/Renderer'
import BilingualTimeline from '@/components/BilingualTimeline'
import JumpToLiveButton from '@/components/JumpToLiveButton'
import useAutoScroll from '@/hooks/useAutoScroll'
import { EyeIcon, SparklesIcon } from '@/components/Icons'
import PanelHeader from './PanelHeader'
import type { RealtimeToken } from '@soniox/client'
import type { MemoAction, MemoActionConflict, MemoKeyPoint } from '@ai-meeting/shared'
import { actionToText } from '@/utils/mapMemoToMeetingInsights'
import ConfirmActionConflictAlert from '@/pages/Meetings/components/ConfirmActionConflictAlert'
import { conflictKey } from '@/utils/memoActionConflict'
import {
  COLLAPSED_STRIP_PX,
  DEFAULT_PANEL_WIDTHS,
  initialPanelWidths,
  type PanelWidths,
} from './meeting-insights-layout'
import { getLanguageDisplayName } from '@/utils/languageDisplayName'
import type { LiveTranscriptTurn } from '@/utils/liveTranscriptTurns'
import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

interface MeetingInsightsProps {
  sourceLang: string
  targetLang: string
  sourceTokens: RealtimeToken[]
  targetTokens: RealtimeToken[]
  sourceOpen: boolean
  onToggleSource: () => void
  targetOpen: boolean
  onToggleTarget: () => void
  /** See `Renderer.highlightNewestOutput` — off in Meeting mode (BRAI-416). */
  highlightNewestOutput?: boolean
  /** See `BilingualTimeline.enlargeNewestTranslation` — off in Meeting mode. */
  enlargeNewestTranslation?: boolean
  memoOpen: boolean
  onToggleMemo: () => void
  keyPointsOpen: boolean
  onToggleKeyPoints: () => void
  keyPoints?: MemoKeyPoint[]
  liveActions?: MemoAction[]
  memoStatus?: LiveMemoStatus
  memoError?: string | null
  mainContent?: string[]
  onMainContentSave?: (items: string[]) => boolean
  mainContentUpdateStatus?: MainContentUpdateStatus
  mainContentUpdateError?: string | null
  onMainContentSaveTimeout?: () => void
  /** Source-aligned rows combining original, realtime and batch translations. */
  liveTurns?: LiveTranscriptTurn[]
  batchOpen?: boolean
  onToggleBatch?: () => void
}

/** Relative flex-grow weights for Enhanced / Summary / Key Points in analysis flex mode. */
interface AnalysisFlexGrow {
  batch: number
  memo: number
  insights: number
}

type ResizeHandle = 'source-target' | 'transcript-batch' | 'batch-memo' | 'memo-insights'

const DEFAULT_ANALYSIS_FLEX_GROW: AnalysisFlexGrow = {
  batch: 1,
  memo: 1,
  insights: 1,
}

const MIN_TRANSCRIPT_PANEL_PX = 220
const MIN_INSIGHTS_PANEL_PX = 280
const MIN_MEMO_PANEL_PX = 260
const MIN_BATCH_PANEL_PX = 260
const SOURCE_PANE_ANIMATION_MS = 300
const MAIN_CONTENT_UPDATE_TIMEOUT_MS = 5000
const DISABLED_AUTO_SCROLL_ITEMS: string[] = []

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

/** Renders live key points with optional expandable detail. */
function KeyPointList({
  items,
  placeholder,
  loading,
  error,
  loadingLabel,
}: {
  items: MemoKeyPoint[]
  placeholder: string
  loading?: boolean
  error?: string | null
  loadingLabel: string
}) {
  // Keys are derived from the topic text (not the array index) so an expanded
  // item stays open across the periodic full-replace snapshots as long as its
  // topic is unchanged. When the same topic appears more than once we suffix an
  // occurrence counter to keep keys unique without reintroducing index churn
  // for the common single-occurrence case (BRAI-164).
  const itemKeys = useMemo(() => {
    const seen = new Map<string, number>()
    return items.map((item) => {
      const base = item.topic.trim()
      const occurrence = seen.get(base) ?? 0
      seen.set(base, occurrence + 1)
      return occurrence === 0 ? base : `${base}#${occurrence}`
    })
  }, [items])
  const [openKeys, setOpenKeys] = useState<string[]>([])

  useEffect(() => {
    const currentItemKeys = new Set(itemKeys)
    setOpenKeys((current) => current.filter((key) => currentItemKeys.has(key)))
  }, [itemKeys])

  if (loading && items.length === 0) {
    return (
      <p className="text-xs leading-5 sm:text-sm sm:leading-6 text-slate-500 dark:text-[var(--text-tertiary)]">
        {loadingLabel}
      </p>
    )
  }

  if (error && items.length === 0) {
    return <p className="text-xs leading-5 sm:text-sm sm:leading-6 text-red-500">{error}</p>
  }

  if (items.length === 0) {
    return (
      <ul className="space-y-2 text-xs leading-5 sm:text-sm sm:leading-6 text-slate-700 dark:text-[var(--text-secondary)]">
        <li>{placeholder}</li>
      </ul>
    )
  }

  return (
    <>
      <ul className="space-y-2 text-xs leading-5 sm:text-sm sm:leading-6 text-slate-700 dark:text-[var(--text-secondary)]">
        {items.map((item, index) => {
          const key = itemKeys[index]
          const isOpen = openKeys.includes(key)
          const hasDetail = Boolean(item.content && item.content !== item.topic)

          return (
            <li key={key} className="rounded-xl border border-transparent">
              <button
                type="button"
                onClick={() =>
                  setOpenKeys((current) =>
                    current.includes(key)
                      ? current.filter((openKey) => openKey !== key)
                      : [...current, key]
                  )
                }
                className="flex w-full items-start gap-2.5 rounded-xl px-2 py-1.5 text-left"
                aria-expanded={isOpen}
              >
                <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500 shadow-[0_0_0_4px_rgba(59,130,246,0.12)] dark:bg-[var(--accent)]" />
                <span className="min-w-0 flex-1">
                  <span className="block break-words text-slate-800 dark:text-[var(--text-primary)]">
                    {item.topic}
                  </span>
                </span>
                <span
                  className={[
                    'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-xs text-blue-600 shadow-sm transition-transform dark:bg-[var(--bg-tertiary)] dark:text-[var(--accent)] sm:text-sm',
                    isOpen ? 'rotate-90' : 'rotate-0',
                  ].join(' ')}
                  aria-hidden="true"
                >
                  ›
                </span>
              </button>

              {hasDetail && (
                <div
                  className={[
                    'grid transition-[grid-template-rows,opacity] duration-200 ease-out',
                    isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
                  ].join(' ')}
                >
                  <div className="overflow-hidden">
                    <p className="mx-2 mb-2 rounded-lg border border-blue-100 bg-white px-3 py-2 text-xs leading-5 text-slate-600 shadow-sm dark:border-[var(--border-color)] dark:bg-[var(--bg-tertiary)] dark:text-[var(--text-secondary)]">
                      {item.content}
                    </p>
                  </div>
                </div>
              )}
            </li>
          )
        })}
      </ul>
      {loading && <InlineLoading label={loadingLabel} />}
      {error && <p className="mt-2 text-xs font-medium text-red-500">{error}</p>}
    </>
  )
}

/** Renders live action items from the memo insight stream. */
function ActionList({
  items,
  placeholder,
  loading,
  error,
  loadingLabel,
}: {
  items: MemoAction[]
  placeholder: string
  loading?: boolean
  error?: string | null
  loadingLabel: string
}) {
  if (loading && items.length === 0) {
    return (
      <p className="text-xs leading-5 sm:text-sm sm:leading-6 text-slate-500 dark:text-[var(--text-tertiary)]">
        {loadingLabel}
      </p>
    )
  }

  if (error && items.length === 0) {
    return <p className="text-xs leading-5 sm:text-sm sm:leading-6 text-red-500">{error}</p>
  }

  if (items.length === 0) {
    return (
      <p className="text-xs leading-5 sm:text-sm sm:leading-6 text-slate-700 dark:text-[var(--text-secondary)]">
        {placeholder}
      </p>
    )
  }

  return (
    <>
      <ul className="space-y-2 text-xs leading-5 sm:text-sm sm:leading-6 text-slate-700 dark:text-[var(--text-secondary)]">
        {items.map((item, index) => (
          <li
            key={`${item.content}-${item.priority}-${index}`}
            className="flex items-start gap-2.5 break-words"
          >
            <span
              className="mt-2 flex h-3 w-3 shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-[var(--accent-light)]"
              aria-hidden="true"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-blue-600 dark:bg-[var(--accent)]" />
            </span>
            <span className="min-w-0 flex-1">{actionToText(item)}</span>
          </li>
        ))}
      </ul>
      {loading && <InlineLoading label={loadingLabel} />}
      {error && <p className="mt-2 text-xs font-medium text-red-500">{error}</p>}
    </>
  )
}

/** Renders the pencil glyph used to enter Summary editing mode. */
function PencilIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <path
        d="m15.75 5.25 3 3m-1.5-4.5 3 3L8.25 18.75l-4.5 1.5 1.5-4.5 12-12Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Converts the textarea draft into the paragraph array expected by liveMemo.
 *
 * @param draft Summary text where blank lines separate paragraphs.
 * @returns Trimmed non-empty paragraphs ready for rendering and persistence.
 */
/**
 * Renders the live `main_content` paragraphs returned by the AI summary
 * endpoint. Each item is a self-contained sentence/paragraph.
 */
function MainContentBlock({
  items,
  placeholder,
  loading,
  loadingLabel,
  editingIndex,
  draft,
  isAwaitingUpdate,
  updateStatus,
  updateError,
  editLabel,
  saveLabel,
  cancelLabel,
  onEditItem,
  onDraftChange,
  onSave,
  onCancel,
  tooLongLabel,
}: {
  items: string[]
  placeholder: string
  loading?: boolean
  loadingLabel: string
  editingIndex: number | null
  draft: string
  isAwaitingUpdate: boolean
  updateStatus: MainContentUpdateStatus
  updateError?: string | null
  editLabel: string
  saveLabel: string
  cancelLabel: string
  onEditItem?: (index: number) => void
  onDraftChange: (draft: string) => void
  onSave: () => void
  onCancel: () => void
  tooLongLabel: string
}) {
  if (items.length === 0) {
    if (loading) {
      return (
        <p className="text-xs leading-5 sm:text-sm sm:leading-6 text-slate-500 dark:text-[var(--text-tertiary)]">
          {loadingLabel}
        </p>
      )
    }
    return (
      <p className="text-xs leading-5 sm:text-sm sm:leading-6 text-slate-700 dark:text-[var(--text-secondary)]">
        {placeholder}
      </p>
    )
  }

  return (
    <ul
      className="space-y-2 text-xs leading-5 sm:text-sm sm:leading-6 text-slate-700 dark:text-[var(--text-secondary)]"
      data-testid="live-main-content"
    >
      {items.map((item, index) => {
        const isEditing = editingIndex === index
        const itemEditLabel = `${editLabel} ${index + 1}`

        return (
          <li key={`${item}-${index}`} className="flex items-start gap-2.5 break-words">
            <span
              className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500 dark:bg-[var(--accent)]"
              aria-hidden="true"
            />
            {isEditing ? (
              <div className="min-w-0 flex-1 space-y-3">
                <textarea
                  autoFocus
                  value={draft}
                  onChange={(event) =>
                    onDraftChange(
                      nextLengthLimitedValue(
                        event.target.value,
                        draft,
                        MAIN_CONTENT_DRAFT_MAX_LENGTH
                      )
                    )
                  }
                  maxLength={MAIN_CONTENT_DRAFT_MAX_LENGTH}
                  aria-label={itemEditLabel}
                  className="input-field min-h-[40px] w-full resize-y text-xs leading-5 sm:text-sm sm:leading-6"
                />
                <p className="text-right text-[10px] font-semibold text-slate-600 dark:text-[var(--text-tertiary)]">
                  {draft.length} / {MAIN_CONTENT_DRAFT_MAX_LENGTH}
                </p>
                {draft.length > MAIN_CONTENT_DRAFT_MAX_LENGTH ? (
                  <p role="alert" className="text-xs font-medium text-red-500">
                    {tooLongLabel}
                  </p>
                ) : null}
                {updateStatus === 'error' && updateError && (
                  <p role="alert" className="text-xs font-medium text-red-500">
                    {updateError}
                  </p>
                )}
                <div className="flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={onCancel}
                    disabled={isAwaitingUpdate}
                    className="btn-ghost min-h-[40px] touch-manipulation px-3 py-2 text-xs sm:text-sm"
                  >
                    {cancelLabel}
                  </button>
                  <button
                    type="button"
                    onClick={onSave}
                    disabled={
                      isAwaitingUpdate ||
                      !draft.trim() ||
                      draft.length > MAIN_CONTENT_DRAFT_MAX_LENGTH
                    }
                    className="btn-primary min-h-[40px] touch-manipulation px-3 py-2 text-xs sm:text-sm"
                  >
                    {saveLabel}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <span className="min-w-0 flex-1 whitespace-pre-wrap text-slate-800 dark:text-[var(--text-primary)]">
                  {item}
                </span>
                {editingIndex === null && onEditItem && (
                  <button
                    type="button"
                    onClick={() => onEditItem(index)}
                    aria-label={itemEditLabel}
                    title={itemEditLabel}
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-blue-600 transition-colors hover:bg-blue-100 hover:text-blue-700 dark:text-[var(--accent)] dark:hover:bg-[var(--accent-light)] dark:hover:text-[var(--accent-hover)]"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                )}
              </>
            )}
          </li>
        )
      })}
    </ul>
  )
}

/** Shows a compact inline loading row for live memo updates. */
function InlineLoading({ label }: { label: string }) {
  return (
    <div className="mt-3 flex items-center gap-2 rounded-lg border border-blue-100 bg-white/70 px-3 py-2 text-xs font-medium text-blue-600 dark:border-[var(--border-color)] dark:bg-[var(--bg-tertiary)] dark:text-[var(--accent)]">
      <span className="h-3 w-3 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600 dark:border-[var(--border-color)] dark:border-t-[var(--accent)]" />
      <span>{label}</span>
    </div>
  )
}

/** Renders the shared card header used by the insight sidebar. */
function InsightCardHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-2 text-blue-700 dark:text-[var(--accent)]">
      {icon}
      <p className="text-[11px] font-extrabold uppercase tracking-wide sm:text-xs">{title}</p>
    </div>
  )
}

/**
 * Unified header bar used across every column/panel in the live recording
 * view (Input, Output, Live Memo, Key Points). Keeps icon size, title weight,
 * padding, and action alignment in sync so the columns line up visually.
 */

/**
 * Thin vertical strip rendered in place of a hidden panel. Displays a sparkles
 * icon at the top, the panel name written vertically (top-to-bottom), and an
 * eye icon at the bottom. Clicking anywhere on the strip restores the panel.
 */
function CollapsedPanelStrip({
  label,
  onShow,
  showLabel,
  testId,
}: {
  label: string
  onShow: () => void
  showLabel: string
  testId?: string
}) {
  return (
    <button
      type="button"
      onClick={onShow}
      aria-label={showLabel}
      title={showLabel}
      data-testid={testId}
      className="group hidden h-full w-11 shrink-0 flex-col items-center justify-between gap-3 border-r border-blue-100 bg-blue-50/40 px-1 py-3 text-blue-600 transition-colors last:border-l last:border-r-0 hover:bg-blue-100/60 hover:text-blue-700 dark:border-[var(--border-color)] dark:bg-[var(--bg-tertiary)] dark:text-[var(--accent)] dark:hover:bg-[var(--accent-light)] dark:hover:text-[var(--accent-hover)] lg:flex"
    >
      <SparklesIcon className="h-4 w-4 shrink-0" />
      <span
        className="flex-1 select-none truncate text-xs font-bold tracking-wide text-slate-700 dark:text-[var(--text-secondary)]"
        style={{ writingMode: 'vertical-rl' }}
      >
        {label}
      </span>
      <EyeIcon className="h-4 w-4 shrink-0" />
    </button>
  )
}

type CollapsedPanelChipItem = {
  label: string
  onShow: () => void
  showLabel: string
  testId: string
}

/**
 * Horizontal restore chips for viewports below `lg`. Vertical strips stay
 * desktop-only so narrow/split-screen layouts keep full width for content.
 */
function CollapsedPanelChipBar({ chips }: { chips: CollapsedPanelChipItem[] }) {
  if (chips.length === 0) return null

  return (
    <div
      className="flex shrink-0 flex-wrap gap-2 overflow-x-auto border-b border-blue-100 bg-blue-50/40 px-3 py-2 dark:border-[var(--border-color)] dark:bg-[var(--bg-tertiary)] min-[500px]:flex-nowrap min-[500px]:gap-1 min-[500px]:px-2 lg:hidden"
      data-testid="collapsed-panel-chip-bar"
    >
      {chips.map((chip) => (
        <button
          key={chip.testId}
          type="button"
          onClick={chip.onShow}
          aria-label={chip.showLabel}
          title={chip.showLabel}
          data-testid={chip.testId}
          className="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-md border border-blue-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-50 dark:border-[var(--border-color)] dark:bg-[var(--bg-secondary)] dark:text-[var(--accent)] dark:hover:bg-[var(--accent-light)] min-[500px]:gap-[1px] min-[500px]:px-1.5 min-[500px]:py-1 min-[500px]:text-[11px]"
        >
          {/* Dropped at the breakpoint where the bar stops wrapping: every chip has
              to share one line from there on, and the icon is `shrink-0`, so its
              width is the only thing left to give back to the label. */}
          <EyeIcon className="h-3.5 w-3.5 shrink-0 min-[500px]:hidden" />
          <span className="truncate">{chip.label}</span>
        </button>
      ))}
    </div>
  )
}

function ResizeDivider({
  position,
  hidden = false,
  active = false,
  onPointerDown,
  anchorLeftPx = null,
}: {
  position: 'inside' | 'transcript-batch' | 'batch-memo' | 'memo-insights'
  hidden?: boolean
  /**
   * Being dragged right now, so it is drawn as though hovered.
   *
   * The pointer does not stay on a 12px target through a drag — it outruns
   * it between frames, and once the panel it sizes reaches its minimum the
   * handle stops while the pointer keeps going. Left to `group-hover`, the
   * grip blinks out and back for the rest of the gesture.
   */
  active?: boolean
  onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => void
  /** When set, overrides %-based anchoring (analysis flex mode). */
  anchorLeftPx?: number | null
}) {
  if (hidden) return null

  // Dividers are absolutely positioned within the layout container. The
  // transcript-batch boundary must add the width of any collapsed strips that
  // precede it (--transcript-divider-offset) so it tracks the real edge instead
  // of drifting by one strip width whenever a transcript pane is collapsed. The
  // batch-memo boundary builds on that same anchor plus the Output (Batch)
  // panel's own width, since batch sits directly between transcript and memo.
  // Analysis flex mode passes measured `anchorLeftPx` because flex columns have
  // no stable %-width CSS variable.
  const style: CSSProperties =
    anchorLeftPx != null
      ? { left: anchorLeftPx }
      : position === 'inside'
        ? { left: 'var(--source-divider-left)' }
        : position === 'transcript-batch'
          ? { left: 'calc(var(--transcript-width) + var(--transcript-divider-offset, 0px))' }
          : position === 'batch-memo'
            ? {
                left: 'calc(var(--transcript-width) + var(--transcript-divider-offset, 0px) + var(--batch-width))',
              }
            : { right: 'var(--insights-width)' }
  const translateClass =
    position === 'memo-insights' && anchorLeftPx == null ? 'translate-x-1/2' : '-translate-x-1/2'

  return (
    <button
      type="button"
      aria-label="Resize panel"
      onPointerDown={onPointerDown}
      style={style}
      className={[
        'group absolute top-0 z-20 hidden h-full w-3 cursor-col-resize touch-none items-stretch justify-center lg:flex',
        translateClass,
      ].join(' ')}
    >
      <span
        className={[
          'h-full w-px transition-colors group-hover:bg-blue-400 group-focus-visible:bg-blue-500',
          active ? 'bg-blue-400' : 'bg-blue-100',
        ].join(' ')}
      />
      <span
        className={[
          'absolute top-1/2 h-10 w-1 -translate-y-1/2 rounded-full bg-blue-300 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100',
          active ? 'opacity-100' : 'opacity-0',
        ].join(' ')}
        data-testid={active ? 'resize-grip-active' : undefined}
      />
    </button>
  )
}

/** Renders the check icon used for action item cards. */
function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2.75a9.25 9.25 0 1 0 0 18.5 9.25 9.25 0 0 0 0-18.5Zm4.03 7.28a.75.75 0 0 0-1.06-1.06l-4.22 4.22-1.72-1.72a.75.75 0 1 0-1.06 1.06l2.25 2.25c.3.3.77.3 1.06 0l4.75-4.75Z"
      />
    </svg>
  )
}

/**
 * Displays live transcript panes (source/target each toggleable, both hidden
 * by default for live recording), a center Live Memo column that renders the
 * AI `main_content` paragraphs, plus the real-time key points / actions
 * sidebar.
 */
export default function MeetingInsights({
  sourceLang,
  targetLang,
  sourceTokens,
  targetTokens,
  sourceOpen,
  highlightNewestOutput = true,
  enlargeNewestTranslation = true,
  onToggleSource,
  targetOpen,
  onToggleTarget,
  memoOpen,
  onToggleMemo,
  keyPointsOpen,
  onToggleKeyPoints,
  keyPoints = [],
  liveActions = [],
  memoStatus = 'idle',
  memoError = null,
  mainContent = [],
  onMainContentSave,
  mainContentUpdateStatus = 'idle',
  mainContentUpdateError = null,
  onMainContentSaveTimeout,
  liveTurns = [],
  batchOpen = false,
  onToggleBatch = () => {},
}: MeetingInsightsProps) {
  const { t } = useUiStrings()
  const isLoading = memoStatus === 'loading'
  const [editingMainContentIndex, setEditingMainContentIndex] = useState<number | null>(null)
  const [mainContentDraft, setMainContentDraft] = useState('')
  const [isAwaitingMainContentUpdate, setIsAwaitingMainContentUpdate] = useState(false)

  /** Captures one visible Summary item as the initial edit draft. */
  const startMainContentEdit = useCallback(
    (index: number) => {
      setEditingMainContentIndex(index)
      setMainContentDraft(mainContent[index] ?? '')
      setIsAwaitingMainContentUpdate(false)
    },
    [mainContent]
  )

  /** Discards the current Summary draft and returns to read-only mode. */
  const cancelMainContentEdit = useCallback(() => {
    setEditingMainContentIndex(null)
    setMainContentDraft('')
    setIsAwaitingMainContentUpdate(false)
  }, [])

  const [dismissedConflicts, setDismissedConflicts] = useState<string[]>([])

  /**
   * The Backlog disagreements to warn about, newest wording per ticket (BRAI-549).
   *
   * Hoisted out of the confirm-items rather than drawn inside the list: the warning is about a
   * ticket, and a reader scanning a live meeting should not have to find the row first. Deduplicated
   * because the same disagreement can be attached to more than one item in a cycle.
   */
  const visibleConflicts = useMemo(() => {
    const byKey = new Map<string, MemoActionConflict>()
    for (const action of liveActions) {
      if (action.conflict) byKey.set(conflictKey(action.conflict), action.conflict)
    }
    for (const key of dismissedConflicts) byKey.delete(key)
    return [...byKey.values()]
  }, [liveActions, dismissedConflicts])

  const dismissConflict = useCallback((conflict: MemoActionConflict) => {
    const key = conflictKey(conflict)
    setDismissedConflicts((current) =>
      current.includes(key) ? current : [...current, key]
    )
  }, [])

  /** Replaces one item in the latest Summary and waits for the server acknowledgment. */
  const saveMainContentEdit = useCallback(() => {
    if (editingMainContentIndex === null) return
    if (mainContentDraft.length > MAIN_CONTENT_DRAFT_MAX_LENGTH) return

    const nextItems = [...mainContent]
    nextItems[editingMainContentIndex] = mainContentDraft.trim()
    if (!onMainContentSave?.(nextItems)) return

    setIsAwaitingMainContentUpdate(true)
  }, [editingMainContentIndex, mainContent, mainContentDraft, onMainContentSave])

  useEffect(() => {
    if (!isAwaitingMainContentUpdate) return

    if (mainContentUpdateStatus === 'success') {
      setIsAwaitingMainContentUpdate(false)
      setEditingMainContentIndex(null)
      setMainContentDraft('')
      return
    }

    if (mainContentUpdateStatus === 'error') {
      setIsAwaitingMainContentUpdate(false)
    }
  }, [isAwaitingMainContentUpdate, mainContentUpdateStatus])

  useEffect(() => {
    if (!isAwaitingMainContentUpdate || mainContentUpdateStatus !== 'pending') {
      return
    }

    const timer = window.setTimeout(() => {
      setIsAwaitingMainContentUpdate(false)
      setEditingMainContentIndex(null)
      setMainContentDraft('')
      onMainContentSaveTimeout?.()
    }, MAIN_CONTENT_UPDATE_TIMEOUT_MS)

    return () => window.clearTimeout(timer)
  }, [isAwaitingMainContentUpdate, mainContentUpdateStatus, onMainContentSaveTimeout])
  const openPanelCount =
    (sourceOpen ? 1 : 0) +
    (targetOpen ? 1 : 0) +
    (memoOpen ? 1 : 0) +
    (keyPointsOpen ? 1 : 0) +
    (batchOpen ? 1 : 0)
  const isLastOpenPanel = openPanelCount === 1
  // Before any speech arrives each Renderer pane shows its own idle placeholder.
  // The source/target divider must stay hidden in that state — otherwise the
  // vertical divider line cuts through the empty cards.
  const hasTranscriptTokens = sourceTokens.length > 0 || targetTokens.length > 0
  const sourceTitle = `${getLanguageDisplayName(sourceLang, t)}${t.panelTitleSeparator}${t.input}`
  const targetTitle = `${getLanguageDisplayName(targetLang, t)}${t.panelTitleSeparator}${t.output}`
  const layoutRef = useRef<HTMLDivElement | null>(null)
  const transcriptRef = useRef<HTMLElement | null>(null)
  const batchRef = useRef<HTMLElement | null>(null)
  const memoRef = useRef<HTMLElement | null>(null)
  const keyPointsRef = useRef<HTMLElement | null>(null)
  const [keyPointsAway, setKeyPointsAway] = useState(false)
  const [actionsAway, setActionsAway] = useState(false)
  const [liveMemoAway, setLiveMemoAway] = useState(false)
  const [batchAway, setBatchAway] = useState(false)
  const { ref: keyPointsScrollRef, jumpToLive: jumpKeyPoints } = useAutoScroll(keyPoints, {
    onAwayFromBottomChange: setKeyPointsAway,
  })
  const { ref: actionsScrollRef, jumpToLive: jumpActions } = useAutoScroll(liveActions, {
    onAwayFromBottomChange: setActionsAway,
  })
  const { ref: liveMemoScrollRef, jumpToLive: jumpLiveMemo } = useAutoScroll(
    editingMainContentIndex !== null ? DISABLED_AUTO_SCROLL_ITEMS : mainContent,
    {
      onAwayFromBottomChange: setLiveMemoAway,
    }
  )
  const { ref: batchScrollRef, jumpToLive: jumpBatch } = useAutoScroll(liveTurns, {
    onAwayFromBottomChange: setBatchAway,
    observeContentResize: true,
  })
  const [panelWidths, setPanelWidths] = useState<PanelWidths>(() =>
    initialPanelWidths({ sourceOpen, targetOpen, batchOpen, memoOpen, keyPointsOpen })
  )
  const [analysisFlexGrow, setAnalysisFlexGrow] =
    useState<AnalysisFlexGrow>(DEFAULT_ANALYSIS_FLEX_GROW)
  const [flexDividerLeftPx, setFlexDividerLeftPx] = useState<{
    batchMemo: number | null
    memoInsights: number | null
  }>({ batchMemo: null, memoInsights: null })
  // While actively dragging a divider we drop the panes' width/flex transitions
  // so they track the pointer exactly instead of easing toward each target.
  /**
   * Which divider is being dragged, or null.
   *
   * A boolean would do for suppressing transitions, but not for keeping the
   * dragged handle lit: its grip and its colour are `group-hover`, and a
   * 12px target cannot hold the pointer through a drag. The pointer outruns
   * it between frames, and once the panel hits its minimum the handle stops
   * dead while the pointer carries on — so the grip blinks out and back for
   * the rest of the gesture.
   */
  const [activeHandle, setActiveHandle] = useState<ResizeHandle | null>(null)
  const isResizing = activeHandle !== null
  // Source/target visibility is controlled by the parent so toggle buttons can
  // live alongside the recording controls. We still track a `rendered` flag
  // locally so the pane can animate out before unmounting.
  const [sourceRendered, setSourceRendered] = useState(sourceOpen)
  const [targetRendered, setTargetRendered] = useState(targetOpen)

  const resizePanels = useCallback(
    (handle: ResizeHandle, clientX: number) => {
      const layoutEl = layoutRef.current
      const transcriptEl = transcriptRef.current
      if (!layoutEl) return

      const layoutRect = layoutEl.getBoundingClientRect()
      const layoutWidth = layoutRect.width
      if (layoutWidth <= 0) return

      // Meeting triad: Enhanced + Summary (+ Key Points) share space via flex-grow.
      const inAnalysisFlexMode = !sourceOpen && !targetOpen && batchOpen && memoOpen

      if (inAnalysisFlexMode && (handle === 'batch-memo' || handle === 'memo-insights')) {
        const batchEl = batchRef.current
        const memoEl = memoRef.current
        if (!batchEl || !memoEl) return

        const batchRect = batchEl.getBoundingClientRect()
        const memoRect = memoEl.getBoundingClientRect()
        const insightsEl = keyPointsOpen ? keyPointsRef.current : null
        const insightsRect = insightsEl?.getBoundingClientRect()
        const analysisLeft = batchRect.left
        const analysisRight = insightsRect?.right ?? memoRect.right
        const totalPx = analysisRight - analysisLeft
        if (totalPx <= 0) return

        setAnalysisFlexGrow((current) => {
          if (handle === 'batch-memo') {
            const minTrailing =
              MIN_MEMO_PANEL_PX + (keyPointsOpen ? MIN_INSIGHTS_PANEL_PX : 0)
            const batchPx = clamp(
              clientX - analysisLeft,
              MIN_BATCH_PANEL_PX,
              Math.max(MIN_BATCH_PANEL_PX, totalPx - minTrailing)
            )
            const trailingPx = totalPx - batchPx
            if (!keyPointsOpen) {
              return { ...current, batch: batchPx, memo: Math.max(MIN_MEMO_PANEL_PX, trailingPx) }
            }
            const trailingGrow = current.memo + current.insights
            const memoShare = trailingGrow > 0 ? current.memo / trailingGrow : 0.5
            let memoPx = trailingPx * memoShare
            let insightsPx = trailingPx - memoPx
            if (memoPx < MIN_MEMO_PANEL_PX) {
              memoPx = MIN_MEMO_PANEL_PX
              insightsPx = trailingPx - memoPx
            }
            if (insightsPx < MIN_INSIGHTS_PANEL_PX) {
              insightsPx = MIN_INSIGHTS_PANEL_PX
              memoPx = trailingPx - insightsPx
            }
            return {
              batch: batchPx,
              memo: Math.max(MIN_MEMO_PANEL_PX, memoPx),
              insights: Math.max(MIN_INSIGHTS_PANEL_PX, insightsPx),
            }
          }

          // memo-insights
          if (!keyPointsOpen || !insightsRect) return current
          const minLeading = MIN_BATCH_PANEL_PX + MIN_MEMO_PANEL_PX
          const insightsPx = clamp(
            analysisRight - clientX,
            MIN_INSIGHTS_PANEL_PX,
            Math.max(MIN_INSIGHTS_PANEL_PX, totalPx - minLeading)
          )
          const leadingPx = totalPx - insightsPx
          const leadingGrow = current.batch + current.memo
          const batchShare = leadingGrow > 0 ? current.batch / leadingGrow : 0.5
          let batchPx = leadingPx * batchShare
          let memoPx = leadingPx - batchPx
          if (batchPx < MIN_BATCH_PANEL_PX) {
            batchPx = MIN_BATCH_PANEL_PX
            memoPx = leadingPx - batchPx
          }
          if (memoPx < MIN_MEMO_PANEL_PX) {
            memoPx = MIN_MEMO_PANEL_PX
            batchPx = leadingPx - memoPx
          }
          return {
            batch: Math.max(MIN_BATCH_PANEL_PX, batchPx),
            memo: Math.max(MIN_MEMO_PANEL_PX, memoPx),
            insights: insightsPx,
          }
        })
        return
      }

      setPanelWidths((current) => {
        if (handle === 'source-target') {
          if (!sourceOpen || !targetOpen) return current
          if (!transcriptEl) return current

          const transcriptRect = transcriptEl.getBoundingClientRect()
          const transcriptWidth = transcriptRect.width
          if (transcriptWidth < MIN_TRANSCRIPT_PANEL_PX * 2) return current

          const sourcePx = clamp(
            clientX - transcriptRect.left,
            MIN_TRANSCRIPT_PANEL_PX,
            transcriptWidth - MIN_TRANSCRIPT_PANEL_PX
          )
          const transcriptPercent = current.source + current.target
          const sourceRatio = sourcePx / transcriptWidth
          const source = transcriptPercent * sourceRatio

          return {
            ...current,
            source,
            target: transcriptPercent - source,
          }
        }

        const openTranscriptCount = (sourceOpen ? 1 : 0) + (targetOpen ? 1 : 0)
        const insightsActive = keyPointsOpen && current.insights > 0
        const minInsightsPercent = insightsActive ? (MIN_INSIGHTS_PANEL_PX / layoutWidth) * 100 : 0
        const minMemoPercent = memoOpen ? (MIN_MEMO_PANEL_PX / layoutWidth) * 100 : 0
        const minBatchPercent = batchOpen ? (MIN_BATCH_PANEL_PX / layoutWidth) * 100 : 0

        if (handle === 'transcript-batch') {
          if (openTranscriptCount === 0 || !transcriptEl) return current

          // Measure from the transcript section's own left edge so a leading
          // collapsed strip never offsets the math. Work entirely in pixels and
          // reserve room for every collapsed strip after it, the Bilingual
          // conversation panel, and any open memo/key-points panes before
          // converting back to a percentage.
          const transcriptRect = transcriptEl.getBoundingClientRect()
          const leadingStripPx = transcriptRect.left - layoutRect.left
          const trailingStripPx = targetOpen ? 0 : COLLAPSED_STRIP_PX
          const collapsedInsightsStripPx =
            (memoOpen ? 0 : COLLAPSED_STRIP_PX) + (keyPointsOpen ? 0 : COLLAPSED_STRIP_PX)
          const insightsPx = keyPointsOpen ? (current.insights / 100) * layoutWidth : 0
          // What the Enhanced column takes out of the row: its width while it
          // is open, its collapsed strip while it is not. Reserving nothing
          // for the strip let the transcript be dragged a strip's width too
          // far, pushing Summary under its own minimum.
          const batchReservePx = batchOpen
            ? memoOpen
              ? (current.batch / 100) * layoutWidth
              : MIN_BATCH_PANEL_PX
            : COLLAPSED_STRIP_PX
          const minTranscriptPx = MIN_TRANSCRIPT_PANEL_PX * openTranscriptCount
          const maxTranscriptPx = Math.max(
            minTranscriptPx,
            layoutWidth -
              leadingStripPx -
              trailingStripPx -
              collapsedInsightsStripPx -
              (memoOpen ? MIN_MEMO_PANEL_PX : 0) -
              insightsPx -
              batchReservePx
          )
          // The pointer grabs the HANDLE, and the handle is drawn past every
          // collapsed strip that follows the transcript — a folded Output
          // pane and a folded Enhanced panel both sit between the transcript's
          // own right edge and the boundary the reader sees. Setting the
          // width to the raw pointer distance puts the transcript's edge
          // under the pointer and the handle a strip's width to the right of
          // it, so it jumps away on the first move.
          const trailingOffsetPx =
            trailingStripPx + (batchOpen ? 0 : COLLAPSED_STRIP_PX)
          const transcriptPx = clamp(
            clientX - transcriptRect.left - trailingOffsetPx,
            minTranscriptPx,
            maxTranscriptPx
          )
          const transcriptPercent = (transcriptPx / layoutWidth) * 100

          // Distribute the new transcript width to whichever panes are open. With
          // both open we preserve their split ratio; with one open we resize that
          // single pane and leave the hidden pane's stored width untouched.
          if (sourceOpen && targetOpen) {
            const previousTranscriptPercent = current.source + current.target
            const sourceRatio =
              previousTranscriptPercent > 0 ? current.source / previousTranscriptPercent : 0.5
            const source = transcriptPercent * sourceRatio

            return {
              source,
              target: transcriptPercent - source,
              insights: current.insights,
              batch: current.batch,
            }
          }

          if (sourceOpen) return { ...current, source: transcriptPercent }
          return { ...current, target: transcriptPercent }
        }

        if (handle === 'batch-memo') {
          // Drag the boundary between the Output (Batch) panel and Live Memo.
          // Measured from the batch section's own left edge (via `batchRef`) so
          // whatever precedes it — the transcript's current width, a collapsed
          // target strip — never offsets the math.
          if (!batchOpen || !memoOpen) return current
          const batchEl = batchRef.current
          if (!batchEl) return current

          const batchRect = batchEl.getBoundingClientRect()
          const visibleTranscriptPercent =
            (sourceOpen ? current.source : 0) + (targetOpen ? current.target : 0)
          const batchPercent = clamp(
            ((clientX - batchRect.left) / layoutWidth) * 100,
            minBatchPercent,
            Math.max(
              minBatchPercent,
              100 - visibleTranscriptPercent - minMemoPercent - current.insights
            )
          )

          return { ...current, batch: batchPercent }
        }

        // memo-insights: drag the boundary between Live Memo and Key Points.
        const visibleTranscriptPercent =
          (sourceOpen ? current.source : 0) + (targetOpen ? current.target : 0)
        const visibleBatchPercent = batchOpen ? current.batch : 0
        const insightsPercent = clamp(
          ((layoutRect.right - clientX) / layoutWidth) * 100,
          minInsightsPercent,
          Math.max(
            minInsightsPercent,
            100 - visibleTranscriptPercent - visibleBatchPercent - minMemoPercent
          )
        )

        return {
          source: current.source,
          target: current.target,
          insights: insightsPercent,
          batch: current.batch,
        }
      })
    },
    [batchOpen, keyPointsOpen, memoOpen, sourceOpen, targetOpen]
  )

  const startResize = useCallback(
    (handle: ResizeHandle, event: ReactPointerEvent<HTMLButtonElement>) => {
      if (window.matchMedia('(max-width: 1023px)').matches) return

      event.preventDefault()
      const pointerId = event.pointerId
      // setPointerCapture is unavailable in some environments (e.g. jsdom); the
      // window-level listeners keep the drag working regardless.
      event.currentTarget.setPointerCapture?.(pointerId)
      setActiveHandle(handle)

      // Lock text selection and force the resize cursor globally so the drag
      // feels continuous even when the pointer leaves the thin divider.
      const previousUserSelect = document.body.style.userSelect
      const previousCursor = document.body.style.cursor
      document.body.style.userSelect = 'none'
      document.body.style.cursor = 'col-resize'

      const onPointerMove = (moveEvent: PointerEvent) => {
        resizePanels(handle, moveEvent.clientX)
      }
      const stopResize = () => {
        window.removeEventListener('pointermove', onPointerMove)
        window.removeEventListener('pointerup', stopResize)
        window.removeEventListener('pointercancel', stopResize)
        document.body.style.userSelect = previousUserSelect
        document.body.style.cursor = previousCursor
        setActiveHandle(null)
      }

      window.addEventListener('pointermove', onPointerMove)
      window.addEventListener('pointerup', stopResize, { once: true })
      window.addEventListener('pointercancel', stopResize, { once: true })
    },
    [resizePanels]
  )

  // Re-clamp the stored percentages whenever the container shrinks or panels
  // open/close. The widths are persisted as percentages, but every column has a
  // pixel minimum (`MIN_*_PANEL_PX`); on a narrow viewport the raw percentages
  // can demand more pixels than the layout has, which pushes the right-most
  // (Key Points) panel past the `overflow-hidden` edge. Capping the width-driven
  // panels so the Live Memo always keeps its minimum keeps every panel inside
  // the container instead of overflowing it.
  useEffect(() => {
    const layoutEl = layoutRef.current
    if (!layoutEl || typeof ResizeObserver === 'undefined') return undefined

    const clampToWidth = (layoutWidth: number) => {
      if (layoutWidth <= 0) return

      // Below lg the row is a column and desktop strips are `hidden`; do not
      // spend budget on COLLAPSED_STRIP_PX or shrink width-driven percentages.
      // Restore any pane that a previous desktop clamp drove to ~0 so opening
      // Input/Output (and resizing back to lg) still has a usable width.
      if (window.matchMedia('(max-width: 1023px)').matches) {
        setPanelWidths((current) => {
          const next: PanelWidths = { ...current }
          if (sourceOpen && current.source < 0.01) next.source = DEFAULT_PANEL_WIDTHS.source
          if (targetOpen && current.target < 0.01) next.target = DEFAULT_PANEL_WIDTHS.target
          if (next.source === current.source && next.target === current.target) return current
          return next
        })
        return
      }

      const openTranscriptCount = (sourceOpen ? 1 : 0) + (targetOpen ? 1 : 0)
      const collapsedStripPx =
        (sourceOpen ? 0 : COLLAPSED_STRIP_PX) +
        (targetOpen ? 0 : COLLAPSED_STRIP_PX) +
        (memoOpen ? 0 : COLLAPSED_STRIP_PX) +
        (keyPointsOpen ? 0 : COLLAPSED_STRIP_PX) +
        (batchOpen ? 0 : COLLAPSED_STRIP_PX)
      const stripPercent = (collapsedStripPx / layoutWidth) * 100

      setPanelWidths((current) => {
        const transcriptPct = (sourceOpen ? current.source : 0) + (targetOpen ? current.target : 0)
        const insightsPct = keyPointsOpen ? current.insights : 0
        const batchUsesWidth = batchOpen && memoOpen
        const batchPct = batchUsesWidth ? current.batch : 0
        const minMemoPct = memoOpen ? (MIN_MEMO_PANEL_PX / layoutWidth) * 100 : 0
        const minInsightsPct =
          keyPointsOpen && insightsPct > 0 ? (MIN_INSIGHTS_PANEL_PX / layoutWidth) * 100 : 0
        const minBatchPct =
          batchUsesWidth && batchPct > 0 ? (MIN_BATCH_PANEL_PX / layoutWidth) * 100 : 0
        const minTranscriptPct =
          openTranscriptCount > 0
            ? (MIN_TRANSCRIPT_PANEL_PX * openTranscriptCount * 100) / layoutWidth
            : 0

        // Budget available to the width-driven columns once strips and the memo
        // minimum are reserved.
        const budget = Math.max(0, 100 - stripPercent - minMemoPct)
        if (transcriptPct + insightsPct + batchPct <= budget + 0.01) return current

        // Shrink insights first (down to its own minimum), then batch, then
        // the transcript.
        let nextInsightsPct = insightsPct
        let nextBatchPct = batchPct
        let nextTranscriptPct = transcriptPct
        const overflow = transcriptPct + insightsPct + batchPct - budget

        const insightsRoom = Math.max(0, insightsPct - minInsightsPct)
        const insightsCut = Math.min(insightsRoom, overflow)
        nextInsightsPct -= insightsCut

        let remaining = overflow - insightsCut

        const batchRoom = Math.max(0, batchPct - minBatchPct)
        const batchCut = Math.min(batchRoom, remaining)
        nextBatchPct -= batchCut
        remaining -= batchCut

        if (remaining > 0) {
          const transcriptRoom = Math.max(0, transcriptPct - minTranscriptPct)
          const transcriptCut = Math.min(transcriptRoom, remaining)
          nextTranscriptPct -= transcriptCut
          remaining -= transcriptCut
        }

        // When the viewport is too narrow to honour every MIN_*_PANEL_PX at
        // once, the per-minimum cuts above cannot absorb all the overflow.
        // Distribute the leftover proportionally across every column so the
        // total still fits the container — a too-small panel is preferable to
        // one spilling past the overflow-hidden edge.
        if (remaining > 0.01) {
          const combined = nextInsightsPct + nextBatchPct + nextTranscriptPct
          if (combined > 0) {
            const scale = Math.max(0, (combined - remaining) / combined)
            nextInsightsPct *= scale
            nextBatchPct *= scale
            nextTranscriptPct *= scale
          }
        }

        const next: PanelWidths = { ...current }
        if (keyPointsOpen) next.insights = nextInsightsPct
        if (batchOpen) next.batch = nextBatchPct
        if (transcriptPct > 0 && nextTranscriptPct !== transcriptPct) {
          const ratio = nextTranscriptPct / transcriptPct
          if (sourceOpen) next.source = current.source * ratio
          if (targetOpen) next.target = current.target * ratio
        }

        if (sourceOpen && next.source < 0.01) {
          next.source = current.source > 0.01 ? current.source : DEFAULT_PANEL_WIDTHS.source
        }
        if (targetOpen && next.target < 0.01) {
          next.target = current.target > 0.01 ? current.target : DEFAULT_PANEL_WIDTHS.target
        }

        if (
          next.source === current.source &&
          next.target === current.target &&
          next.insights === current.insights &&
          next.batch === current.batch
        ) {
          return current
        }
        return next
      })
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) clampToWidth(entry.contentRect.width)
    })
    observer.observe(layoutEl)
    clampToWidth(layoutEl.getBoundingClientRect().width)

    return () => observer.disconnect()
  }, [sourceOpen, targetOpen, memoOpen, keyPointsOpen, batchOpen])

  useEffect(() => {
    if (sourceOpen) {
      setSourceRendered(true)
      return undefined
    }

    const timeoutId = window.setTimeout(() => {
      setSourceRendered(false)
    }, SOURCE_PANE_ANIMATION_MS)

    return () => window.clearTimeout(timeoutId)
  }, [sourceOpen])

  useEffect(() => {
    if (targetOpen) {
      setTargetRendered(true)
      return undefined
    }

    const timeoutId = window.setTimeout(() => {
      setTargetRendered(false)
    }, SOURCE_PANE_ANIMATION_MS)

    return () => window.clearTimeout(timeoutId)
  }, [targetOpen])

  const isSourceVisible = sourceOpen && sourceRendered
  const isTargetVisible = targetOpen && targetRendered
  // Transcript section is fully hidden when both source and target are off.
  const transcriptVisible = isSourceVisible || isTargetVisible
  // Only include the width of panes that are actually OPEN (visible). A pane
  // that was opened then hidden still has `sourceRendered === true`, but its
  // slot must not contribute width — otherwise the visible pane's header (and
  // its eye-off button) drifts toward the next column.
  const transcriptPercent = transcriptVisible
    ? (isSourceVisible ? panelWidths.source : 0) + (isTargetVisible ? panelWidths.target : 0)
    : 0
  const transcriptDenominator =
    panelWidths.source + panelWidths.target > 0 ? panelWidths.source + panelWidths.target : 1
  const sourcePanePercent = sourceRendered
    ? sourceOpen
      ? targetOpen
        ? (panelWidths.source / transcriptDenominator) * 100
        : 100
      : 0
    : 0
  const targetPanePercent = targetRendered
    ? targetOpen
      ? sourceOpen
        ? (panelWidths.target / transcriptDenominator) * 100
        : 100
      : 0
    : 0
  // The transcript-batch divider is anchored at `--transcript-width` from the
  // layout's left edge, but every collapsed strip between that edge and the
  // panel the divider actually borders sits in between. A collapsed source
  // pane (leading strip) and a collapsed target pane (trailing strip) both
  // do, and so does a collapsed Enhanced panel — which is the one that was
  // missing: with Enhanced folded the divider landed one strip short of
  // Summary's left edge, where the boundary really is.
  const transcriptDividerOffsetPx =
    (sourceOpen ? 0 : COLLAPSED_STRIP_PX) +
    (targetOpen ? 0 : COLLAPSED_STRIP_PX) +
    (batchOpen ? 0 : COLLAPSED_STRIP_PX)
  const batchFillsRemainingRow = batchOpen && !memoOpen
  // Meeting default row: Input/Output collapsed → Enhanced|Summary|Key Points
  // share remaining space equally via flex-grow (not fixed %-widths).
  const analysisFlexMode = !transcriptVisible && batchOpen && memoOpen
  // Transcript is width-driven whenever a memo or Bilingual conversation panel
  // shares the row, so its right-hand divider can resize it — including when
  // only one raw transcript pane is open. Otherwise it fills the remaining row.
  const transcriptUsesWidth = transcriptVisible && (memoOpen || batchOpen)
  /**
   * One divider per gap between consecutive open panels, named by which of
   * the three can actually describe that gap.
   *
   * Keyed to fixed pairs, folding Summary took both dividers it was named in,
   * and Key Points — whose only divider was `memo-insights` — was left with
   * no handle on either side. Deriving them from what is open fixes that, but
   * the name cannot simply follow the panel on the left, because the three
   * anchor in different ways:
   *
   * - `transcript-batch` sits on the transcript's right edge.
   * - `batch-memo` sits at `--batch-width`, which only describes the Enhanced
   *   panel while Summary is open. With Summary folded that panel flex-fills
   *   the row, and the variable points into the middle of it — a stray line
   *   across an open panel.
   * - `memo-insights` sits on Key Points' left edge, measured from the right,
   *   so it is correct whatever precedes it.
   *
   * Hence: a gap that ends at Key Points always uses `memo-insights`, and
   * `batch-memo` is only ever the gap between Enhanced and Summary.
   */
  const openRowBlocks = (
    [
      transcriptVisible ? 'transcript' : null,
      batchOpen ? 'batch' : null,
      memoOpen ? 'memo' : null,
      keyPointsOpen ? 'insights' : null,
    ] as const
  ).filter((block): block is 'transcript' | 'batch' | 'memo' | 'insights' => block !== null)
  const gapDividers = new Set<'transcript-batch' | 'batch-memo' | 'memo-insights'>()
  for (let index = 0; index < openRowBlocks.length - 1; index += 1) {
    const right = openRowBlocks[index + 1]
    if (right === 'insights') gapDividers.add('memo-insights')
    else if (openRowBlocks[index] === 'transcript') gapDividers.add('transcript-batch')
    else gapDividers.add('batch-memo')
  }

  const bottomRowOpen = memoOpen || keyPointsOpen
  // Narrow column: each open panel flex-fills the viewport and scrolls internally.
  const narrowBatchClass = batchOpen ? 'max-lg:flex-1 max-lg:min-h-0' : 'max-lg:hidden'
  // <640: Summary then Key Points stacked; sm–lg: side by side. lg+: wrapper is
  // `contents` so memo/keypoints remain desktop row peers.
  const narrowBottomClass = bottomRowOpen
    ? 'max-lg:flex max-lg:flex-1 max-lg:min-h-0 max-lg:flex-col sm:max-lg:flex-row'
    : 'max-lg:hidden'
  const collapsedChips: CollapsedPanelChipItem[] = [
    !sourceOpen
      ? {
          label: sourceTitle,
          onShow: onToggleSource,
          showLabel: t.showInput,
          testId: 'collapsed-chip-source',
        }
      : null,
    !targetOpen
      ? {
          label: targetTitle,
          onShow: onToggleTarget,
          showLabel: t.showOutput,
          testId: 'collapsed-chip-target',
        }
      : null,
    !batchOpen
      ? {
          label: t.bilingualTimeline,
          onShow: onToggleBatch,
          showLabel: t.showBilingualTimeline,
          testId: 'collapsed-chip-timeline',
        }
      : null,
    !memoOpen
      ? {
          label: t.liveMemo,
          onShow: onToggleMemo,
          showLabel: t.showLiveMemo,
          testId: 'collapsed-chip-memo',
        }
      : null,
    !keyPointsOpen
      ? {
          label: t.realTimeKeyPoints,
          onShow: onToggleKeyPoints,
          showLabel: t.showKeyPoints,
          testId: 'collapsed-chip-keypoints',
        }
      : null,
  ].filter((chip): chip is CollapsedPanelChipItem => chip !== null)

  useLayoutEffect(() => {
    if (!analysisFlexMode) {
      setFlexDividerLeftPx({ batchMemo: null, memoInsights: null })
      return undefined
    }

    const layoutEl = layoutRef.current
    if (!layoutEl) return undefined

    const syncDividers = () => {
      const layout = layoutRef.current
      const batchEl = batchRef.current
      if (!layout || !batchEl) return
      const layoutLeft = layout.getBoundingClientRect().left
      const batchRight = batchEl.getBoundingClientRect().right - layoutLeft
      const insightsEl = keyPointsOpen ? keyPointsRef.current : null
      const memoInsightsLeft = insightsEl
        ? insightsEl.getBoundingClientRect().left - layoutLeft
        : null
      setFlexDividerLeftPx({
        batchMemo: batchRight,
        memoInsights: memoInsightsLeft,
      })
    }

    syncDividers()
    if (typeof ResizeObserver === 'undefined') return undefined

    const observer = new ResizeObserver(syncDividers)
    observer.observe(layoutEl)
    if (batchRef.current) observer.observe(batchRef.current)
    if (memoRef.current) observer.observe(memoRef.current)
    if (keyPointsRef.current) observer.observe(keyPointsRef.current)
    return () => observer.disconnect()
  }, [analysisFlexMode, analysisFlexGrow, keyPointsOpen, isResizing])

  const layoutStyle = {
    '--transcript-width': `${transcriptPercent}%`,
    '--insights-width': `${panelWidths.insights}%`,
    '--batch-width': `${panelWidths.batch}%`,
    '--source-divider-left': `${sourcePanePercent}%`,
    '--transcript-divider-offset': `${transcriptDividerOffsetPx}px`,
  } as CSSProperties

  const batchFlexStyle: CSSProperties | undefined = analysisFlexMode
    ? { flex: `${analysisFlexGrow.batch} 1 0%`, minWidth: MIN_BATCH_PANEL_PX }
    : undefined
  const memoFlexStyle: CSSProperties | undefined = analysisFlexMode
    ? { flex: `${analysisFlexGrow.memo} 1 0%`, minWidth: MIN_MEMO_PANEL_PX }
    : undefined
  const insightsFlexStyle: CSSProperties | undefined =
    analysisFlexMode && keyPointsOpen
      ? { flex: `${analysisFlexGrow.insights} 1 0%`, minWidth: MIN_INSIGHTS_PANEL_PX }
      : undefined

  return (
    <div
      ref={layoutRef}
      className="relative flex h-full min-h-0 flex-1 flex-col bg-white dark:bg-[var(--bg-secondary)] max-lg:overflow-hidden lg:flex-row lg:overflow-hidden"
      style={layoutStyle}
      data-testid="meeting-insights-layout"
    >
      <CollapsedPanelChipBar chips={collapsedChips} />

      {!sourceOpen && (
        <CollapsedPanelStrip
          label={sourceTitle}
          onShow={onToggleSource}
          showLabel={t.showInput}
          testId="collapsed-strip-source"
        />
      )}
      <section
        ref={transcriptRef}
        className={[
          'relative flex min-h-0 flex-col overflow-hidden',
          isResizing ? 'transition-none' : 'transition-[width,opacity] duration-300 ease-out',
          transcriptVisible
            ? transcriptUsesWidth
              ? 'opacity-100 max-lg:flex-1 max-lg:min-h-0 lg:w-[var(--transcript-width)] lg:flex-none'
              : 'opacity-100 max-lg:flex-1 max-lg:min-h-0 lg:flex-1'
            : 'pointer-events-none max-lg:hidden w-0 opacity-0',
        ].join(' ')}
        aria-hidden={!transcriptVisible}
      >
        {/*
          Headers live inside Renderer columns (header+body per pane) so narrow
          viewports stack Input→content→Output→content instead of both headers
          then a shared content region.
        */}
        <Renderer
          sourceTokens={sourceTokens}
          targetTokens={targetTokens}
          sourcePaneOpen={sourceOpen}
          sourcePaneRendered={hasTranscriptTokens || sourceRendered}
          targetPaneOpen={targetOpen}
          targetPaneRendered={hasTranscriptTokens || targetRendered}
          highlightNewestOutput={highlightNewestOutput}
          sourcePanePercent={sourcePanePercent}
          targetPanePercent={targetPanePercent}
          sourceHeader={
            sourceOpen ? (
              <PanelHeader
                icon={<SparklesIcon className="h-5 w-5" />}
                title={sourceTitle}
                onHide={onToggleSource}
                hideLabel={t.hideInput}
                hideDisabled={isLastOpenPanel}
              />
            ) : null
          }
          targetHeader={
            targetOpen ? (
              <PanelHeader
                icon={<SparklesIcon className="h-5 w-5" />}
                title={targetTitle}
                onHide={onToggleTarget}
                hideLabel={t.hideOutput}
                hideDisabled={isLastOpenPanel}
              />
            ) : null
          }
        />
        <ResizeDivider
          position="inside"
          active={activeHandle === 'source-target'}
          // Shown as soon as both panes are, rather than waiting for the
          // first words. It used to wait so its line could not cross the two
          // "Start speaking" placeholders, but the line falls on the border
          // between the panes, which is where those cards already stop — and
          // the cost of waiting was a column pair that could not be sized
          // until somebody spoke.
          hidden={!sourceOpen || !targetOpen}
          onPointerDown={(event) => startResize('source-target', event)}
        />
      </section>

      {!targetOpen && (
        <CollapsedPanelStrip
          label={targetTitle}
          onShow={onToggleTarget}
          showLabel={t.showOutput}
          testId="collapsed-strip-target"
        />
      )}

      <ResizeDivider
        position="transcript-batch"
        active={activeHandle === 'transcript-batch'}
        hidden={!gapDividers.has('transcript-batch')}
        onPointerDown={(event) => startResize('transcript-batch', event)}
      />

      {/* Primary conversation view: source and batch translation are aligned by
          source segment ID in one chronological row stream. The legacy
          Input/Output panes remain available via their collapsed strips. */}
      {!batchOpen && (
        <CollapsedPanelStrip
          label={t.bilingualTimeline}
          onShow={onToggleBatch}
          showLabel={t.showBilingualTimeline}
          testId="collapsed-strip-timeline"
        />
      )}

      <section
        ref={batchRef}
        className={[
          'flex min-h-0 min-w-0 flex-col overflow-hidden border-blue-100 bg-white dark:border-[var(--border-color)] dark:bg-[var(--bg-secondary)]',
          isResizing ? 'transition-none' : 'transition-[width,opacity,flex] duration-300 ease-out',
          narrowBatchClass,
          batchOpen
            ? analysisFlexMode
              ? 'min-h-0 min-w-0 border-l opacity-100 max-lg:flex-1 max-lg:min-h-0 max-lg:border-l-0 max-lg:border-t'
              : batchFillsRemainingRow
                ? 'min-h-0 min-w-0 flex-1 border-l opacity-100 max-lg:border-l-0 max-lg:border-t'
                : 'min-h-0 min-w-0 border-l opacity-100 max-lg:flex-1 max-lg:min-h-0 max-lg:border-l-0 max-lg:border-t max-lg:w-auto lg:w-[var(--batch-width)] lg:flex-none lg:shrink-0'
            : 'pointer-events-none w-0 shrink-0 border-l-0 opacity-0 lg:opacity-0',
        ].join(' ')}
        style={batchFlexStyle}
        aria-hidden={!batchOpen}
        aria-label={t.bilingualTimeline}
        data-testid="live-timeline-column"
      >
        <PanelHeader
          icon={<SparklesIcon className="h-5 w-5" />}
          title={t.bilingualTimeline}
          onHide={onToggleBatch}
          hideLabel={t.hideBilingualTimeline}
          hideDisabled={isLastOpenPanel}
        />
        <div className="relative flex min-h-0 flex-1 flex-col">
          <div
            ref={batchScrollRef}
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3 [overflow-wrap:anywhere] sm:px-4 sm:py-5"
            data-testid="live-timeline-scroll"
          >
            <BilingualTimeline
              turns={liveTurns}
              enlargeNewestTranslation={enlargeNewestTranslation}
            />
          </div>
          <JumpToLiveButton
            show={batchAway}
            onClick={jumpBatch}
            label={t.jumpToLive}
            testId="jump-to-live-timeline"
          />
        </div>
      </section>

      <ResizeDivider
        position="batch-memo"
        active={activeHandle === 'batch-memo'}
        hidden={!gapDividers.has('batch-memo')}
        anchorLeftPx={analysisFlexMode ? flexDividerLeftPx.batchMemo : null}
        onPointerDown={(event) => startResize('batch-memo', event)}
      />

      {!memoOpen && (
        <CollapsedPanelStrip
          label={t.liveMemo}
          onShow={onToggleMemo}
          showLabel={t.showLiveMemo}
          testId="collapsed-strip-memo"
        />
      )}

      {/* Below lg: Summary | Key Points share one row (stack under sm). At lg,
          `contents` dissolves the wrapper so memo/keypoints stay row peers. */}
      <div
        className={['min-h-0 lg:contents', narrowBottomClass].join(' ')}
        data-testid="meeting-insights-bottom-row"
      >
        {/* Live Memo column: toggleable via parent; collapses to zero width when
            hidden so the remaining columns fill the row. */}
        <section
          ref={memoRef}
          className={[
            'flex min-h-0 min-w-0 flex-col overflow-hidden border-blue-100 bg-white dark:border-[var(--border-color)] dark:bg-[var(--bg-secondary)]',
            isResizing ? 'transition-none' : 'transition-[flex,opacity] duration-300 ease-out',
            memoOpen
              ? analysisFlexMode
                ? 'min-w-0 border-l opacity-100 max-lg:min-h-0 max-lg:flex-1 max-lg:border-t max-lg:border-l-0 lg:border-l'
                : 'flex-1 opacity-100 max-lg:min-h-0 max-lg:flex-1 max-lg:border-t max-lg:border-l-0 lg:border-l'
              : 'pointer-events-none max-lg:hidden flex-none basis-0 opacity-0',
          ].join(' ')}
          style={memoFlexStyle}
          aria-hidden={!memoOpen}
          aria-label={t.liveMemo}
          data-testid="live-memo-column"
        >
          <PanelHeader
            icon={<SparklesIcon className="h-5 w-5" />}
            title={t.liveMainContent}
            onHide={onToggleMemo}
            hideLabel={t.hideLiveMemo}
            hideDisabled={isLastOpenPanel}
          />
          <div className="relative flex min-h-0 flex-1 flex-col">
            <div
              ref={liveMemoScrollRef}
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3 sm:px-5 sm:py-4"
              data-testid="live-memo-scroll"
            >
              <MainContentBlock
                items={mainContent}
                placeholder={t.liveMainContentPlaceholder}
                loading={isLoading}
                loadingLabel={t.memoInsightsLoading}
                editingIndex={editingMainContentIndex}
                draft={mainContentDraft}
                isAwaitingUpdate={isAwaitingMainContentUpdate}
                updateStatus={mainContentUpdateStatus}
                updateError={mainContentUpdateError}
                editLabel={t.editMainContent}
                saveLabel={t.saveMainContent}
                cancelLabel={t.cancelMainContentEdit}
                onEditItem={onMainContentSave ? startMainContentEdit : undefined}
                onDraftChange={setMainContentDraft}
                onSave={saveMainContentEdit}
                onCancel={cancelMainContentEdit}
                tooLongLabel={fmt(t.fieldTooLong, { max: MAIN_CONTENT_DRAFT_MAX_LENGTH })}
              />
            </div>
            <JumpToLiveButton
              show={editingMainContentIndex === null && liveMemoAway}
              onClick={jumpLiveMemo}
              label={t.jumpToLive}
              testId="jump-to-live-live-memo"
            />
          </div>
        </section>

        <ResizeDivider
          position="memo-insights"
          active={activeHandle === 'memo-insights'}
          hidden={!gapDividers.has('memo-insights')}
          anchorLeftPx={analysisFlexMode ? flexDividerLeftPx.memoInsights : null}
          onPointerDown={(event) => startResize('memo-insights', event)}
        />

        {!keyPointsOpen && (
          <CollapsedPanelStrip
            label={t.realTimeKeyPoints}
            onShow={onToggleKeyPoints}
            showLabel={t.showKeyPoints}
            testId="collapsed-strip-keypoints"
          />
        )}

        <aside
          ref={keyPointsRef}
          className={[
            'flex min-h-0 min-w-0 flex-col overflow-hidden border-blue-100 bg-slate-50 dark:border-[var(--border-color)] dark:bg-[var(--bg-tertiary)]',
            isResizing ? 'transition-none' : 'transition-[width,opacity,flex] duration-300 ease-out',
            keyPointsOpen
              ? analysisFlexMode
                ? 'min-h-0 min-w-0 border-l opacity-100 max-lg:min-h-0 max-lg:min-w-0 max-lg:flex-1 max-lg:border-l max-lg:border-t'
                : !transcriptVisible && !memoOpen && !batchOpen
                  ? 'min-w-0 flex-1 border-l opacity-100 max-lg:border-l-0 max-lg:border-t'
                  : 'min-h-0 min-w-0 border-l opacity-100 max-lg:min-h-0 max-lg:min-w-0 max-lg:flex-1 max-lg:border-l max-lg:border-t max-lg:w-auto sm:max-lg:border-l lg:shrink-0 lg:w-[var(--insights-width)]'
              : 'pointer-events-none max-lg:hidden w-0 shrink-0 border-l-0 opacity-0',
          ].join(' ')}
          style={insightsFlexStyle}
          aria-hidden={!keyPointsOpen}
          data-testid="live-keypoints-column"
        >
          <PanelHeader
            icon={<SparklesIcon className="h-5 w-5" />}
            title={t.realTimeKeyPoints}
            onHide={onToggleKeyPoints}
            hideLabel={t.hideKeyPoints}
            hideDisabled={isLastOpenPanel}
          />
          <div className="grid min-h-0 min-w-0 flex-1 grid-rows-[minmax(0,1fr)_minmax(0,1fr)] divide-y divide-blue-100 dark:divide-[var(--border-color)]">
            <section className="flex min-h-0 flex-col overflow-hidden">
              <div className="shrink-0 px-3 pb-2 pt-3 sm:px-4 sm:pt-4">
                <InsightCardHeader icon={<SparklesIcon className="h-4 w-4" />} title={t.keyPoints} />
              </div>
              <div className="relative flex min-h-0 flex-1 flex-col">
                <div
                  ref={keyPointsScrollRef}
                  className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-3 sm:px-4 sm:pb-4"
                  data-testid="key-points-scroll"
                >
                  <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-3 dark:border-[var(--border-color)] dark:bg-[var(--accent-light)] sm:p-4">
                    <KeyPointList
                      items={keyPoints}
                      placeholder={t.keyPointsPlaceholder}
                      loading={isLoading}
                      error={memoError ? t.memoInsightsError : null}
                      loadingLabel={t.memoInsightsLoading}
                    />
                  </div>
                </div>
                <JumpToLiveButton
                  show={keyPointsAway}
                  onClick={jumpKeyPoints}
                  label={t.jumpToLive}
                  testId="jump-to-live-key-points"
                />
              </div>
            </section>
            <section className="flex min-h-0 flex-col overflow-hidden">
              <div className="shrink-0 px-3 pb-2 pt-3 sm:px-4 sm:pt-4">
                <InsightCardHeader
                  icon={<CheckCircleIcon className="h-4 w-4" />}
                  title={t.confirmAction}
                />
              </div>
              <div className="relative flex min-h-0 flex-1 flex-col">
                <div
                  ref={actionsScrollRef}
                  className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-3 sm:px-4 sm:pb-4"
                  data-testid="actions-scroll"
                >
                  <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-3 dark:border-[var(--border-color)] dark:bg-[var(--accent-light)] sm:p-4">
                    <ActionList
                      items={liveActions}
                      placeholder={t.actionItemPlaceholder}
                      loading={isLoading}
                      error={memoError ? null : undefined}
                      loadingLabel={t.memoInsightsLoading}
                    />
                  </div>
                  {visibleConflicts.map((conflict) => (
                    <ConfirmActionConflictAlert
                      key={conflictKey(conflict)}
                      conflict={conflict}
                      onDismiss={() => dismissConflict(conflict)}
                    />
                  ))}
                </div>
                <JumpToLiveButton
                  show={actionsAway}
                  onClick={jumpActions}
                  label={t.jumpToLive}
                  testId="jump-to-live-actions"
                />
              </div>
            </section>
          </div>
        </aside>
      </div>
    </div>
  )
}
